import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const RoomChatSocketContext = createContext(null);

export const useRoomChatSocket = () => {
  const context = useContext(RoomChatSocketContext);
  if (!context) {
    throw new Error('useRoomChatSocket must be used within a RoomChatSocketProvider');
  }
  return context;
};

export const RoomChatSocketProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const isUnmountedRef = useRef(false);
  const connectFnRef = useRef(null);
  
  // Message handlers that can be registered by components
  const messageHandlersRef = useRef(new Set());
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Store current values in refs for use in callbacks
  const tokenRef = useRef(token);
  const currentRoomIdRef = useRef(currentRoomId);
  const isAuthenticatedRef = useRef(isAuthenticated);
  
  useEffect(() => {
    tokenRef.current = token;
    isAuthenticatedRef.current = isAuthenticated;
  }, [token, isAuthenticated]);
  
  useEffect(() => {
    currentRoomIdRef.current = currentRoomId;
  }, [currentRoomId]);
  
  // Register a message handler
  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.add(handler);
    return () => {
      messageHandlersRef.current.delete(handler);
    };
  }, []);
  
  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    logger.warn('⚠️ Room Chat WebSocket not connected, cannot send message');
    return false;
  }, []);
  
  // Store connection function in ref to avoid circular dependency
  useEffect(() => {
    connectFnRef.current = (roomId) => {
      if (!tokenRef.current || !isAuthenticatedRef.current || isUnmountedRef.current) {
        return;
      }
      
      // If already connected to this room, do nothing
      if (currentRoomIdRef.current === roomId && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return;
      }
      
      // If connection is in progress to this room, skip
      if (currentRoomIdRef.current === roomId && wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
        return;
      }
      
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      setCurrentRoomId(roomId);
      currentRoomIdRef.current = roomId;
      reconnectAttempts.current = 0;
      
      const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/room-chat/${roomId}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          if (isUnmountedRef.current) {
            ws.close();
            return;
          }
          setConnected(true);
          setReconnecting(false);
          reconnectAttempts.current = 0;
          
          // Authenticate immediately
          ws.send(JSON.stringify({
            type: 'authenticate',
            token: tokenRef.current
          }));
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Notify all registered handlers
            messageHandlersRef.current.forEach(handler => {
              try {
                handler(data);
              } catch (e) {
                logger.error('Error in room chat message handler:', e);
              }
            });
          } catch (e) {
            logger.error('Error parsing room chat message:', e);
          }
        };
        
        ws.onerror = (error) => {
          logger.error('❌ Room Chat WebSocket error:', error);
        };
        
        ws.onclose = (event) => {
          setConnected(false);
          wsRef.current = null;
          
          // Only attempt reconnection if we still have a room and not unmounted
          if (!isUnmountedRef.current && 
              currentRoomIdRef.current && 
              reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            setReconnecting(true);
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (!isUnmountedRef.current && currentRoomIdRef.current && connectFnRef.current) {
                connectFnRef.current(currentRoomIdRef.current);
              }
            }, delay);
          } else {
            setReconnecting(false);
          }
        };
      } catch (error) {
        logger.error('❌ Failed to create Room Chat WebSocket:', error);
      }
    };
  }, [API_URL]);
  
  // Connect to a specific room
  const connectToRoom = useCallback((roomId) => {
    if (connectFnRef.current) {
      connectFnRef.current(roomId);
    }
  }, []);
  
  // Disconnect from current room
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnected(false);
    setCurrentRoomId(null);
    currentRoomIdRef.current = null;
    reconnectAttempts.current = 0;
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    isUnmountedRef.current = false;
    
    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);
  
  // Disconnect when user logs out - handle cleanup without calling disconnect
  useEffect(() => {
    if (!isAuthenticated || !token) {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }
  }, [isAuthenticated, token]);
  
  const value = {
    connected,
    reconnecting,
    // Derived convenience status for ChatStatusIndicator.
    chatStatus: connected
      ? 'connected'
      : reconnecting
        ? 'reconnecting'
        : currentRoomId
          ? 'connecting'
          : 'disconnected',
    currentRoomId,
    sendMessage,
    addMessageHandler,
    connectToRoom,
    disconnect
  };
  
  return (
    <RoomChatSocketContext.Provider value={value}>
      {children}
    </RoomChatSocketContext.Provider>
  );
};

export default RoomChatSocketContext;
